import { useRef, MutableRefObject, useEffect } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import { Theme } from "@mui/material";
import SignaturePad from "react-signature-canvas";
import CachedIcon from "@mui/icons-material/Cached";
import { Box } from "@mui/system";
import { useFormContext, useController } from "react-hook-form";
import { Button } from "@mui/material";
import { SignatureField } from "@flightright/reusable-components";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { useGetPreviousAnswers } from "@/core/hooks/useGetPreviousAnswers/useGetPreviousAnswers";
import { Typography } from "@mui/material";
import { FieldProps } from "@/core/components/Field";
import {
  HeadingLevel,
  SignatureData,
  BACKGROUND_COLOR,
  MaterialColors,
} from "@/core/types/types";
import { useUserInfo } from "@/hooks/useUserInfo/useUserInfo";
import { trimMimeType } from "@/utils/base64Utils";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import {
  IP_ADDRESS_FIELD,
  USER_AGENT_FIELD,
  MIN_SIGNATURE_LENGTH,
} from "@/core/data";
import { useFieldError } from "@/core/hooks/useFieldError/useFieldError";

const styles = (
  theme: Theme,
  { signatureHint, resetBtnTopOffset, wrapperBorderColor, wrapperBgColor }: any
) => ({
  customerName: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 1,
    minHeight: 20,
  },
  wrapper: {
    border: `1px solid ${wrapperBorderColor}`,
    backgroundColor: wrapperBgColor ?? theme.palette.background.default,
    height: 200,
    position: "relative",
    "& canvas": {
      width: "100%",
      height: 198,
    },
    "&::after": {
      display: "block",
      position: "absolute",
      width: "100%",
      textAlign: "center",
      top: 10,
      content: `"${signatureHint}"`,
      zIndex: "1",
    },
  },
  restartBtn: {
    top: resetBtnTopOffset,
    display: "flex",
    alignItems: "center",
    position: "absolute",
    "& button": {
      textTransform: "inherit",
    },
  },
});

const SigningPad = ({ field, initialValue = {} }: FieldProps) => {
  const { key, validate, properties } = field;
  const { fullName } = initialValue;
  const theme: Theme = useTheme();
  const isMobile: boolean = useMediaQuery(theme.breakpoints.down("md"));
  const padRef = useRef() as MutableRefObject<SignaturePad>;
  const {
    control,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const {
    field: { onChange },
  } = useController({ name: key });
  const { setValue } = useFormContext();
  const userInfo = useUserInfo();
  const { hasError, errorMessage, errorTranslationKey } = useFieldError(
    errors,
    field
  );
  const signatureHint: string = t("signature.hint");
  const notLongEnoughErrorMessage: string = t(
    `validate.${errorTranslationKey}.notLongEnough.message`
  );
  const bgColor: string | undefined = properties[BACKGROUND_COLOR];
  const wrapperBgColor: string | undefined =
    bgColor === MaterialColors.Primary ? theme.palette.primary.main : bgColor;
  const wrapperBorderColor: string = hasError
    ? theme.palette.error.main
    : theme.palette.secondary.main;
  const resetBtnTopOffset: string = fullName ? "180px" : "160px";
  const classes: any = useCustomStyles(styles, {
    signatureHint,
    resetBtnTopOffset,
    wrapperBorderColor,
    wrapperBgColor,
  });

  useEffect(() => {
    if (userInfo) {
      setValue(IP_ADDRESS_FIELD, userInfo.ipAddress);
      setValue(USER_AGENT_FIELD, userInfo.userAgent);
    }
  }, [userInfo]);

  useEffect(() => {
    const clearSignatureIfNotMobile = () => {
      if (!isMobile) {
        clearSignature();
      }
    };

    window.addEventListener("resize", clearSignatureIfNotMobile);

    return () => {
      window.removeEventListener("resize", clearSignatureIfNotMobile);
    };
  }, []);

  const clearSignature = () => {
    padRef.current.clear();
    onChange("");
    clearErrors(field.key);
  };

  const validatePad = (padRef: MutableRefObject<SignaturePad>) => () => {
    const groups: SignaturePad.Point[][] = padRef.current.toData();
    const isLongEnough: boolean = !!groups.some(
      (group: SignaturePad.Point[]) => group.length > MIN_SIGNATURE_LENGTH
    );
    if (!isLongEnough) {
      return notLongEnoughErrorMessage;
    } else {
      clearErrors(field.key);
      return true;
    }
  };

  const setSignature = (signatureDataUrl: string) => {
    const signature = trimMimeType(signatureDataUrl);
    const signatureData: SignatureData = new SignatureData(signature);

    onChange(signatureData);
  };

  return (
    <>
      <Box
        id={key}
        sx={{
          position: "relative",
        }}
      >
        {fullName && (
          <Box sx={classes?.customerName}>
            <Typography variant={HeadingLevel.H3}>{fullName}</Typography>
          </Box>
        )}

        <Box sx={classes?.wrapper}>
          <SignatureField
            ref={padRef}
            name={key}
            control={control}
            rules={{
              required: {
                value: validate?.required || false,
                message: t(`validate.${errorTranslationKey}.required.message`),
              },
              validate: validatePad(padRef),
            }}
            signaturePadOptions={{
              clearOnResize: false,
            }}
            handleChange={setSignature}
          />
        </Box>
        <Box sx={classes?.restartBtn}>
          <Button
            disableRipple
            disableTouchRipple
            disableElevation
            color="secondary"
            variant="text"
            onClick={clearSignature}
          >
            <CachedIcon></CachedIcon>
            <Box ml={1}>{t("signature.reset")}</Box>
          </Button>
        </Box>
        {hasError && <Typography color="error">{`${errorMessage}`}</Typography>}
      </Box>
    </>
  );
};

const SigningPadWithCamunda = (fieldProps: FieldProps) => {
  const {
    properties: { requiredInputVariables },
  } = fieldProps.field;

  if (!requiredInputVariables) {
    throw new Error("Missing required input variable names.");
  }

  const { result } = useGetPreviousAnswers(requiredInputVariables);
  const { customerFirstName, customerLastName } = result;
  let fullName: string = "";

  if (customerFirstName && customerLastName) {
    fullName = `${customerFirstName} ${customerLastName}`;
  }

  return (
    <SigningPad
      field={fieldProps.field}
      initialValue={{
        fullName,
      }}
    />
  );
};

export { SigningPad, SigningPadWithCamunda };
